// Geometry helpers — the single home for GeoJSON ⇄ Leaflet coordinate order.
// GeoJSON rings are [lng,lat]; Leaflet uses [lat,lng]. Ported from
// latlngsToPolygonCoords (L2382) and the bbox parse in fetchLiveSites (L2291).

export interface LL {
  lat: number;
  lng: number;
}

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface GeoJSONPoint {
  type: 'Point';
  coordinates: [number, number];
}

/** Convert Leaflet [lat,lng] vertices to a closed [lng,lat] GeoJSON ring. */
export function latlngsToPolygonCoords(latlngs: LL[]): [number, number][] {
  const ring = latlngs.map((ll) => [ll.lng, ll.lat] as [number, number]);
  if (ring.length > 0) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
  }
  return ring;
}

/** Build a GeoJSON Polygon from Leaflet [lat,lng] vertices. */
export function latlngsToPolygon(latlngs: LL[]): GeoJSONPolygon {
  return { type: 'Polygon', coordinates: [latlngsToPolygonCoords(latlngs)] };
}

export function pointToGeoJSON(lat: number, lng: number): GeoJSONPoint {
  return { type: 'Point', coordinates: [lng, lat] };
}

/** GeoJSON Polygon (from get_sites_live) → Leaflet [lat,lng] ring. */
export function bboxToLatLngs(
  bbox: { type?: string; coordinates?: number[][][] } | null | undefined,
): [number, number][] | null {
  if (bbox?.type === 'Polygon' && bbox.coordinates?.[0]) {
    return bbox.coordinates[0].map((c) => [c[1], c[0]] as [number, number]);
  }
  return null;
}

/** Hex → rgba() string. Ported from hexa() (L803). */
export function hexa(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Haversine distance in metres between two [lat,lng] points (L789). */
export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLng = (lng2 - lng1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

/**
 * Initial great-circle bearing from point 1 to point 2, in degrees clockwise
 * from true north (0-360). Used to point the Fleet map's in-motion chevrons:
 * the battery feed carries speed but no heading, so direction is derived from
 * consecutive polls.
 */
export function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = Math.PI / 180;
  const f1 = lat1 * toRad;
  const f2 = lat2 * toRad;
  const dL = (lng2 - lng1) * toRad;
  const y = Math.sin(dL) * Math.cos(f2);
  const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dL);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Spherical polygon area in m² from [lat,lng] vertices. Ported from geoArea (L1068). */
export function geoArea(pts: LL[]): number {
  if (pts.length < 3) return 0;
  const R = 6378137;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    a +=
      (((p2.lng - p1.lng) * Math.PI) / 180) *
      (2 + Math.sin((p1.lat * Math.PI) / 180) + Math.sin((p2.lat * Math.PI) / 180));
  }
  return Math.abs((a * R * R) / 2);
}
